import { log, logV } from "../../shared/services/debug-logger";
import { api, apiUrl } from "../../shared/services/service";
import { TECH_FORM_SECTION_ENDPOINTS } from "../models/tech-form-section-data.constants";
import type {
  TechFormEndpointDataRequest,
  TechFormNextSectionPrefetchRequest,
  TechFormSectionDataRequest,
} from "../models/tech-form-section-data.models";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import { getTechFormEndpointCacheKey } from "../utils/get-tech-form-endpoint-cache-key.utils";

const cachedEndpointData = new Map<string, unknown>();
const endpointRequestsInFlight = new Map<string, Promise<unknown>>();
const endpointCacheVersions = new Map<string, number>();
let cacheGeneration = 0;

function invalidateTechFormEndpointCacheKey(cacheKey: string): void {
  endpointCacheVersions.set(cacheKey, (endpointCacheVersions.get(cacheKey) ?? 0) + 1);
  cachedEndpointData.delete(cacheKey);
  endpointRequestsInFlight.delete(cacheKey);
}

export function isTechFormEndpointDataCached(request: TechFormEndpointDataRequest): boolean {
  return cachedEndpointData.has(getTechFormEndpointCacheKey(request));
}

export function isTechFormSectionDataCached(request: TechFormSectionDataRequest): boolean {
  const endpoints = TECH_FORM_SECTION_ENDPOINTS[request.section] ?? [];

  return endpoints.every((endpoint) => isTechFormEndpointDataCached({ ...request, endpoint }));
}

export function loadTechFormEndpointData<T>(request: TechFormEndpointDataRequest): Promise<T> {
  const cacheKey = getTechFormEndpointCacheKey(request);
  if (cachedEndpointData.has(cacheKey)) {
    logV("SERV - loadTechFormEndpointData - cached", { endpoint: request.endpoint });
    return Promise.resolve(cachedEndpointData.get(cacheKey) as T);
  }

  const requestInFlight = endpointRequestsInFlight.get(cacheKey);
  if (requestInFlight) {
    logV("SERV - loadTechFormEndpointData - shared request", { endpoint: request.endpoint });
    return requestInFlight as Promise<T>;
  }

  const requestCacheGeneration = cacheGeneration;
  const requestCacheVersion = endpointCacheVersions.get(cacheKey) ?? 0;
  const endpointRequest = api
    .get<T>(`${apiUrl}/${request.endpoint}`, {
      params: {
        imo_no: request.imoNumber,
        vessel_name: request.vesselName,
      },
    })
    .then((response) => {
      log("REPO - loadTechFormEndpointData - response", { endpoint: request.endpoint, status: response.status });
      if (
        cacheGeneration === requestCacheGeneration &&
        (endpointCacheVersions.get(cacheKey) ?? 0) === requestCacheVersion
      ) {
        cachedEndpointData.set(cacheKey, response.data);
      }
      return response.data;
    })
    .catch((error: unknown) => {
      const response = error && typeof error === "object" && "response" in error ? error.response : null;
      const responseStatus = response && typeof response === "object" && "status" in response ? response.status : null;
      if (responseStatus !== 404) {
        log("REPO - loadTechFormEndpointData - failed", { endpoint: request.endpoint });
        throw error;
      }

      const emptyEndpointData = [] as unknown as T;
      log("SERV - loadTechFormEndpointData - empty", { endpoint: request.endpoint });
      if (
        cacheGeneration === requestCacheGeneration &&
        (endpointCacheVersions.get(cacheKey) ?? 0) === requestCacheVersion
      ) {
        cachedEndpointData.set(cacheKey, emptyEndpointData);
      }
      return emptyEndpointData;
    })
    .finally(() => {
      if (endpointRequestsInFlight.get(cacheKey) === endpointRequest) {
        endpointRequestsInFlight.delete(cacheKey);
      }
    });

  endpointRequestsInFlight.set(cacheKey, endpointRequest);
  return endpointRequest;
}

export async function prefetchNextTechFormSectionData(request: TechFormNextSectionPrefetchRequest): Promise<void> {
  const nextSection = request.currentSection + 1;
  const endpoints = TECH_FORM_SECTION_ENDPOINTS[nextSection] ?? [];
  const results = await Promise.allSettled(
    endpoints.map((endpoint) => loadTechFormEndpointData({ ...request, endpoint })),
  );

  const failed = results.filter((result) => result.status === "rejected").length;
  logV("SERV - prefetchNextTechFormSectionData - completed", { section: nextSection, failed });
}

export function invalidateTechFormSectionDataCache(request: TechFormSectionDataRequest): void {
  logV("STATE - invalidateTechFormSectionDataCache", { section: request.section, mode: request.mode });
  const endpoints = TECH_FORM_SECTION_ENDPOINTS[request.section] ?? [];
  endpoints.forEach((endpoint) => {
    invalidateTechFormEndpointCacheKey(getTechFormEndpointCacheKey({ ...request, endpoint }));
  });
}

export function invalidateTechFormSectionDataCacheForAllModes(request: Omit<TechFormSectionDataRequest, "mode">): void {
  Object.values(TECH_FORM_CACHE_MODES).forEach((mode) => {
    invalidateTechFormSectionDataCache({ ...request, mode });
  });
}

export function invalidateTechFormVesselDataCache(request: Omit<TechFormEndpointDataRequest, "endpoint">): void {
  const invalidatedEndpoints = new Set<string>();

  Object.values(TECH_FORM_SECTION_ENDPOINTS).forEach((endpoints) => {
    endpoints.forEach((endpoint) => {
      if (invalidatedEndpoints.has(endpoint)) return;
      invalidatedEndpoints.add(endpoint);
      invalidateTechFormEndpointCacheKey(getTechFormEndpointCacheKey({ ...request, endpoint }));
    });
  });
}

export function clearTechFormSectionDataCache(): void {
  log("STATE - clearTechFormSectionDataCache");
  cacheGeneration += 1;
  cachedEndpointData.clear();
  endpointRequestsInFlight.clear();
  endpointCacheVersions.clear();
}
